import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DOCUMENT, ElementRef, OnInit, Renderer2, ViewChild, afterNextRender, effect, inject, input, output } from '@angular/core';
import { BrowserApiService } from 'src/app/core/services/browser-api/browser-api.service';

import { FormControl } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DynamicHeaderCommon } from 'src/app/components/dynamic-header/category/dynamic-header-common.class';
import { DynamicMenuCollapseComponent } from 'src/app/components/dynamic-menu-collapse/dynamic-menu-collapse.component';
import { SearchBarComponent } from 'src/app/components/search-suggestion/components/search-bar/search-bar.component';
import { StaticMenuCollapseComponent } from 'src/app/components/static-menu-collapse/static-menu-collapse.component';
import { Cart } from 'src/app/core/services/cart/cart-api.service';
import { DeviceDetectionService } from 'src/app/core/services/device-detection';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { safeCartSetter } from 'src/app/core/utilities/cart-fallback.util';
import { createDefaultCart, createDefaultHTMLElement, createDefaultHeaderData } from 'src/app/core/utilities/type-defaults.util';
import { Category } from 'src/app/shared/services/common-api.service';
import { MenuCollapseService } from '../../services/menu-collapse.service';
import { HeaderData } from '../header-data';

@Component({
  selector: 'category-layout-9',
  templateUrl: './layout-9.component.html',
  standalone: true,
  imports: [CommonModule, SearchBarComponent, StaticMenuCollapseComponent, DynamicMenuCollapseComponent, RouterLink],
  styleUrls: ['./layout-9.component.scss']
})
export class Layout9Component extends DynamicHeaderCommon implements OnInit, AfterViewInit {
  protected override userService = inject(UserService);
  private readonly deviceDetectionService = inject(DeviceDetectionService);

  @ViewChild('changeHeaderElement') declare changeHeaderElement: ElementRef;
  @ViewChild('changeHeaderMobileElement') declare changeHeaderMobileElement: ElementRef;
  @ViewChild('dynamicCategoryHeader') declare dynamicCategoryHeader: ElementRef;
  @ViewChild('dynamicCategoryHeaderMobile') declare dynamicCategoryHeaderMobile: ElementRef;



  readonly dynamicHeaderInline = input<boolean>(false);
  readonly dynamicHeaderHover = input<boolean>(false);
  readonly layoutType = input<string>("");
  readonly domain = input<string>('');

  readonly externalDeviceType = input<string | undefined>(undefined);

  // NEW: Expose service signals for template usage
  get isDesktop() { return this.deviceDetectionService.isDesktop; }
  get isMobile() { return this.deviceDetectionService.isMobile; }
  get isTablet() { return this.deviceDetectionService.isTablet; }
  get deviceType() { return this.deviceDetectionService.deviceType; }



  readonly headerDataInput = input<HeaderData>(createDefaultHeaderData());
  readonly categoryInput = input<Category[] | null>(null);
  readonly clickedElementAvailableInput = input<HTMLElement | null>(null);
  readonly cartInput = input<Cart | null>(null);

  // Getters for backward compatibility
  override get headerData(): HeaderData {
    return this._headerData;
  }
  get category(): Category[] {
    return this._category;
  }
  get clickedElementAvailable(): HTMLElement {
    return this._clickedElementAvailable;
  }
  get cart(): Cart {
    return this._cart;
  }

  protected declare _headerData: HeaderData;
  private _category: Category[] = [];
  private _clickedElementAvailable: HTMLElement = createDefaultHTMLElement() as HTMLElement;
  private _cart: Cart = createDefaultCart();

  // REMOVED: selectedDeviceType input setter - service handles everything now

  // Add getter/setter for internal use (not as input)
  override set selectedDeviceType(value: string) {
    // This triggers the parent class logic
    super.selectedDeviceType = value;
  }
  override get selectedDeviceType(): string {
    return super.selectedDeviceType;
  }

  isSubmenuAvailable = true;
  override showSubcategoryPanel = false;
  override searchInputControl = new FormControl<string | null>('');

  // Update output to use Angular 20 output() function
  logout$ = output<boolean>();

  constructor() {
    const browserApiService = inject(BrowserApiService);

    super(
      inject(Renderer2),
      inject(MenuCollapseService),
      inject(ElementRef),
      inject(PlatformService),
      inject<Document>(DOCUMENT),
      browserApiService
    );

    // Effect to handle device type changes and business logic
    effect(() => {
      const deviceInfo = this.deviceDetectionService.getDeviceBusinessInfo();

      // Handle hamburger menu logic
      if (deviceInfo.shouldCloseHamburgerMenu) {
        this.isHamburgerMenu = false;
        this.menuCollapseService.setHamburgerMenu(false);
        this.showMobileSearchPanel = false;
      }

      // Handle repositioning
      if (deviceInfo.needsRepositioning) {
        this.repositionHeaderMenu();
      }

      // Update parent class using the setter (this will trigger parent logic)
      if (deviceInfo.type) {
        // Convert DeviceType enum to string for parent compatibility
        const deviceTypeString = deviceInfo.type.toString();
        this.selectedDeviceType = deviceTypeString;
      }
    });

    // Effect to sync service with existing device type when needed
    effect(() => {
      this.deviceDetectionService.deviceType();
    });

    effect(() => {
      const externalDeviceTypeValue = this.externalDeviceType();
      if (externalDeviceTypeValue) {
        this.deviceDetectionService.updateDeviceTypeFromExternal(externalDeviceTypeValue);
      }
    });

    effect(() => {
      const headerDataValue = this.headerDataInput();
      this._headerData = headerDataValue;
    });

    effect(() => {
      const categoryValue = this.categoryInput();
      if (categoryValue) {
        this._category = categoryValue;

        // ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Angular 20: When category data is available, attach hover listeners
        if (categoryValue.length > 0) {
          afterNextRender(() => {
            this.addHoverForMenu();
          });
        }
      } else {
        this._category = [];
      }
    });

    effect(() => {
      const clickedElementValue = this.clickedElementAvailableInput();
      if (clickedElementValue) {
        this._clickedElementAvailable = clickedElementValue;
      } else {
        this._clickedElementAvailable = createDefaultHTMLElement() as HTMLElement;
      }
    });

    effect(() => {
      const cartValue = this.cartInput();
      this._cart = safeCartSetter(cartValue);
    });
  }

  ngOnInit(): void {
    if (this.platformService.isBrowser()) {

      // Refresh user data from the base class
      this.refreshUserInChild();
      // ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ REMOVED: setTimeout - now handled by categoryInput effect in constructor
    }
  }


  ngAfterViewInit(): void {
    if (this.platformService.isBrowser()) {
      this.repositionHeaderMenu();
      this.applyFocusEvent();
    }
  }

  onLinkClick(): void {
    const subMenus = document.getElementsByClassName("hide-sub-menu-header");
    // Iterate over the elements and set display to none
    Array.from(subMenus).forEach(subMenu => {
      this.renderer.setStyle(subMenu, 'display', 'none');
    });
  }

  override toggleSearchDropDown(): void {
    this.isSearchOverlay = !this.isSearchOverlay;
    this.isHamburgerMenu = false;
    this.searchInputControl.reset();
    if (this.isSearchOverlay) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  override toggleHamburgerMenu(): void {
    this.isToggled = true;
    this.menuCollapseService.toggleHamburgerMenu();
    this.isHamburgerMenu = !this.isHamburgerMenu;
    this.isSearchOverlay = false;
    if (this.isHamburgerMenu) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
    } else {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  searchItems(): void {
    this.toggleSearchDropDown();
  }

  subMenu(): void {
    this.isSubmenuAvailable = true;
  }

  openSubcategoryPanel(sub_categories: Category[] = []): void {
    if (sub_categories?.length) {
      this.showSubcategoryPanel = !this.showSubcategoryPanel;
      this.mobileSubCategory = sub_categories;
    }
  }

  // Implement searchProduct method to handle search input events
  override searchProduct(searchQuery: { keyboardEvent: KeyboardEvent, suggestionItem: any, searchInputValue?: string }): void {
    const { keyboardEvent, suggestionItem } = searchQuery;

    if (suggestionItem) {
      this.searchQuery$.emit(searchQuery);
      this.disableSearchPanel(); // Close panel after suggestion selection
    } else if (keyboardEvent) {
      if (keyboardEvent.key === 'Enter') {
        const inputValue = this.searchInputControl?.value || '';
        this.searchQuery$.emit({ ...searchQuery, searchInputValue: inputValue });
        this.disableSearchPanel(); // Close panel after Enter key press
      }
    }
  }
}

